// useClassesStore.ts
import { create } from 'zustand';
import apiClient from '../services/axiosInterceptor';

interface ClassOption {
  label: string;
  value: string;
  id: string;
  sections: string[];
}

interface ClassResponse {
  id: string;
  class_name: string;
  class_order: number;
  sections: string[];
}

interface ClassesStore {
  classOptions: ClassOption[];
  fetchClasses: (includePlaceholder?: boolean) => Promise<void>;
  hasFetched: boolean;
  isFetching: boolean;
}

const useClassesStore = create<ClassesStore>((set, get) => ({
  classOptions: [],
  hasFetched: false,
  isFetching: false,

  fetchClasses: async (includePlaceholder = false) => {
    const { isFetching, hasFetched, classOptions } = get();

    if (isFetching || hasFetched || classOptions.length > 0) {
      set({ hasFetched: true });
      return;
    }

    set({ isFetching: true });

    try {
      const response = await apiClient.get(`classes?page=1&limit=25`);

      if (response.data.status === 'success') {
        if (response.data.classes.length === 0) {
          const emptyOption = includePlaceholder
            ? [{ label: 'No classes available', value: '', id: '', sections: [] }]
            : [];
          set({ classOptions: emptyOption, hasFetched: true });
        } else {
          const sortedClasses = response.data.classes
            .sort((a: ClassResponse, b: ClassResponse) => a.class_order - b.class_order)
            .map((cls: ClassResponse) => ({
              label: cls.class_name,
              value: cls.class_name,
              id: cls.id,
              sections: cls.sections,
            }));

          const options = includePlaceholder
            ? [{ label: 'Select a Class', value: '', id: '', sections: [] }, ...sortedClasses]
            : sortedClasses;

          set({ classOptions: options, hasFetched: true });
        }
      } else if (response.data.message === 'empty') {
        const emptyOption = includePlaceholder
          ? [{ label: 'No classes available', value: '', id: '', sections: [] }]
          : [];
        set({ classOptions: emptyOption, hasFetched: true });
      }
      // No toast on failure — silently mark as fetched
    } catch (error: any) {
      // Let your interceptor handle 401s (e.g., redirect to /login)
      // Other errors are silently ignored per your request
    } finally {
      set({ isFetching: false, hasFetched: true });
    }
  },
}));

export default useClassesStore;